import { type ReactNode, useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Dialog.module.css';

interface Props {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** For tables: a wider dialog. */
  wide?: boolean;
  'data-testid'?: string;
}

/**
 * A modal dialog (native <dialog>): a title with a close button over the content. Escape, the
 * close button and a click on the backdrop all close it.
 */
export function Dialog({ open, title, onClose, children, wide, 'data-testid': testId }: Props) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className={wide ? `${styles.dialog} ${styles.wide}` : styles.dialog}
      aria-labelledby={titleId}
      onClose={onClose}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
      data-testid={testId}
    >
      {open && (
        <div className={styles.content}>
          <div className={styles.header}>
            <h2 id={titleId}>{title}</h2>
            <button type="button" className={styles.close} onClick={onClose} aria-label={t('common.close')}>
              ×
            </button>
          </div>
          {children}
        </div>
      )}
    </dialog>
  );
}
